import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PersonGlyph } from "./PersonGlyph";

const GLYPH_COLORS = ["#3B82F6", "#FDE047", "#FBF7EE"];

const GlyphRow = ({ reverse = false }) => (
    <div className="flex shrink-0 items-center">
        {Array.from({ length: 36 }).map((_, i) => (
            <span key={i} className="mx-5">
                <PersonGlyph color={GLYPH_COLORS[i % 3]} size={22} />
            </span>
        ))}
    </div>
);

export const FinalCTA = ({ onOpenModal }) => {
    const [today, setToday] = useState(312);

    useEffect(() => {
        const iv = setInterval(() => setToday((t) => t + 1), 2800);
        return () => clearInterval(iv);
    }, []);

    return (
        <section id="contact" data-testid="final-cta-section" className="relative overflow-hidden bg-ink py-28 text-paper lg:py-36">
            <div className="pointer-events-none absolute left-0 top-5 w-full overflow-hidden opacity-[0.18]">
                <div className="marquee-track-slow flex w-max">
                    <GlyphRow />
                    <GlyphRow />
                </div>
            </div>
            <div className="pointer-events-none absolute bottom-5 left-0 w-full overflow-hidden opacity-[0.18]">
                <div className="marquee-track-slow flex w-max" style={{ animationDirection: "reverse" }}>
                    <GlyphRow />
                    <GlyphRow />
                </div>
            </div>

            <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                >
                    <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-hl">The queue never stops</p>
                    <h2 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                        Ready to Build<br />Your <span className="text-hl">Queue?</span>
                    </h2>
                    <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-paper/70 sm:text-lg">
                        Let's turn attention into traffic, traffic into customers, and customers into growth.
                    </p>

                    <div
                        data-testid="live-queue-counter"
                        className="mx-auto mt-8 inline-flex items-center gap-2.5 border-2 border-paper/25 px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] text-paper/80"
                    >
                        <span className="pulse-dot h-2 w-2 rounded-full bg-hl" />
                        {today.toLocaleString()} customers delivered today — and counting
                    </div>

                    <div className="mt-10">
                        <button
                            data-testid="start-project-button"
                            onClick={onOpenModal}
                            className="btn-primary group inline-flex items-center gap-3 border-2 border-hl bg-hl px-9 py-5 font-mono text-sm font-bold uppercase tracking-wider text-ink"
                        >
                            Start a Project
                            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                        </button>
                    </div>
                    <p className="mt-6 rotate-[-2deg] font-hand text-2xl text-hl/90">
                        every few seconds, someone else's queue grows — make the next one yours
                    </p>
                </motion.div>
            </div>
        </section>
    );
};
